import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// CAT # found (assigned, or proposed by the cross-reference) but the order's clamp type differs from the catalog —
// ONE click updates the order's clamp codes from the catalog item (rule Jun 2026). Who clicked is recorded.
export function ClampAdjustCard({ order, mismatch, catNumber, compact = false }) {
  const qc = useQueryClient();
  const cat = catNumber || order.catalog_number;
  const adjust = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/adjust-clamps-to-catalog`, catNumber && catNumber !== order.catalog_number ? { catalog_number: catNumber } : {}),
    onSuccess: (r) => {
      qc.invalidateQueries();
      const ch = r?.data?.changes || [];
      toast.success(ch.length ? `Clamps updated from CAT # ${cat} · ${ch.map((c) => `${c.label.replace(/^F1 /, "")} ${c.from} → ${c.to}`).join(" · ")}` : `Clamps already match CAT # ${cat}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update the clamps"),
  });
  if (!mismatch?.length) return null;
  const slot = (mismatch[0].slot || "f1").toUpperCase();
  const button = (
    <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={adjust.isPending} onClick={() => adjust.mutate()}
            title={`Set the order's clamp codes to the ${slot} clamps registered on CAT # ${cat}`} data-testid={compact ? `clamp-adjust-btn-${cat}` : "clamp-adjust-btn"}>
      {adjust.isPending ? "Updating…" : "✓ Update clamps from catalog"}
    </Button>
  );
  if (compact) {
    return (
      <div className="w-full flex flex-wrap items-center gap-2 text-xs font-mono" data-testid={`clamp-mismatch-${cat}`}>
        <span className="text-[10px] uppercase tracking-widest font-sans font-black text-amber-800">⚠ clamps differ</span>
        {mismatch.map((m) => (
          <span key={m.field} className="px-2 py-1 rounded bg-white border border-amber-300 text-slate-800" data-testid={`clamp-mismatch-${cat}-${m.field}`}>
            {m.label}: <b className="text-red-700">{m.order_value || "—"}</b> → <b className="text-emerald-700">{m.catalog_value}</b>
          </span>
        ))}
        <span className="ml-auto">{button}</span>
      </div>
    );
  }
  return (
    <div className="mb-3 rounded-md border-2 border-amber-500 bg-amber-50 p-3" data-testid="clamp-mismatch-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[11px] font-black uppercase tracking-widest text-amber-800">⚠ Clamp type differs from catalog · {slot} clamps</div>
          <div className="mt-1 flex flex-wrap gap-2 text-xs font-mono">
            {mismatch.map((m) => (
              <span key={m.field} className="px-2 py-1 rounded bg-white border border-amber-300 text-slate-800" data-testid={`clamp-mismatch-${m.field}`}>
                {m.label}: <b className="text-red-700">{m.order_value || "—"}</b> → <b className="text-emerald-700">{m.catalog_value}</b>
              </span>
            ))}
          </div>
          <div className="text-[11px] text-slate-600 mt-1">
            CAT # <b className="font-mono">{cat}</b> is registered with these clamps. One click copies them onto the order — your name is recorded in the order history.
          </div>
        </div>
        {button}
      </div>
    </div>
  );
}
